// @flow

export type Keys = string | string[];

export const planTypes = Object.freeze({
  free: 'Free',
  starter: 'Starter',
  professional: 'Professional',
  team: 'Team',
  company: 'Company',
});

export type PlanType = $Values<typeof planTypes>;

export const changeTypes = Object.freeze({
  change: 'change',
  downgrade: 'downgrade',
  upgrade: 'upgrade',
});

export type ChangeType = $Keys<typeof changeTypes>;

export const intervalTypes = Object.freeze({
  month: 'month',
  year: 'year',
});

export type Interval = $Keys<typeof intervalTypes>;

export const flagTypes = Object.freeze({
  australia: 'Australia',
  brazil: 'Brazil',
  canada: 'Canada',
  europeanUnion: 'EuropeanUnion',
  greatBritain: 'GreatBritain',
  india: 'India',
  japan: 'Japan',
  newZealand: 'NewZealand',
  unitedStates: 'UnitedStates',
});

export type Flag = $Values<typeof flagTypes>;

export type Currency = {
  icon?: Flag,
  multiplier: number,
  name: string,
  symbol: string,
};

export type PlanIntervalProps = {
  amount: number,
  id: number,
  interval: Interval,
  monthlyAmount: number,
};

export type TaskLimitVariant = {
  intervals: Array<PlanIntervalProps>,
  name: string,
  tasks: number,
};

type PlanLimits = {
  premium_apps_limit: number,
  zap_limit: number,
  action_limit: number,
};

export type AccountPlanTier = [number, number];

export type AccountPlan = {
  actions: number,
  allow_branching_zaps: boolean,
  allow_companies: boolean,
  allow_premium_apps: boolean,
  allow_teams: boolean,
  apps: number,
  authentications: number,
  can_autoreplay_errored_tasks: boolean,
  intervals: Array<PlanIntervalProps>,
  limits: PlanLimits,
  max_metering_charge: number,
  meta: Array<*>,
  metered_pricing_tiers: ?Array<AccountPlanTier>,
  metered_task_pricing: boolean,
  min_seats: number,
  name: string,
  off_plan_group?: boolean,
  per_extra_action: ?number,
  plan_type: PlanType,
  polling_interval_minutes: number,
  trial_period_days: ?number,
  task_limit_variants: Array<TaskLimitVariant>,
  type: string,
  zap_length: number,
  zaps: number,
};

export type FeatureListing = {
  name: string,
  tooltip?: string,
};

export type Feature = {
  hasFeature: AccountPlan => boolean,
  helpText?: string,
  helpUrl?: string,
  key: string,
  name: string,
};

export type Features = {
  hasAutoReplay?: boolean,
  hasPaths?: boolean,
  hasDeveloperApps?: boolean,
  hasMeteredBilling?: boolean,
  hasMultiStepZaps?: boolean,
  hasPremiumApps?: boolean,
  hasTeams?: boolean,
};

export type PlanTier = {
  numTasks: number,
  pricePerTask: number,
};

export type QuestionProps = {|
  isChecked?: boolean,
  question: string,
|};

export type Plan = {
  ...$Exact<Features>,
  allowCompanies: boolean,
  amount: number,
  billingAmount: number,
  billingInterval: Interval,
  changePlanUrl?: string,
  description?: string,
  descriptiveName: string,
  displayName: string,
  features?: Array<FeatureListing>,
  featuresTag?: string,
  hasTeams: boolean,
  id: number,
  image?: string,
  intervals: Array<PlanIntervalProps>,
  isCurrentPlan?: boolean,
  isInPlanGroup: boolean,
  metered_pricing_tiers?: Array<any>,
  name: string,
  selectedTaskLimitVariant: TaskLimitVariant,
  shortName: string,
  taskLimitVariants: Array<TaskLimitVariant>,
  tasks: number,
  tiers: Array<PlanTier>,
  type: PlanType,
  zaps: number,
  zapsRunInterval: number,
};

export type PlanListingProps = {
  currency: Currency,
  currentPlan?: Plan,
  onPurchaseNow?: Plan => void,
  onSelect: (
    selectedPlan: Plan,
    planChangeType: ChangeType,
    isFall2019Pricing?: boolean
  ) => void,
  plans: Array<Plan>,
  trialingPlanId?: number,
};
